import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Scissors } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/utils/api';
import { fmtH } from '../agendaUtils';
import { FreeHours } from './FreeHours';

const DURATIONS = [[30, '30 min'], [60, '1 h'], [120, '2 h'], [180, '3 h']];
const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-CH', { weekday: 'long', day: '2-digit', month: '2-digit' });

const Suggestions = ({ list, onPick }) => (
  <div className="mt-2 space-y-1.5" data-testid="slot-suggestions">
    <p className="text-xs font-semibold text-slate-600">Prochains créneaux compatibles :</p>
    {list.map((s, i) => (
      <button key={i} type="button" onClick={() => onPick(s)} data-testid={`slot-suggestion-${i}`}
        className="w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors">
        <span className="font-semibold capitalize">{fmtDate(s.date)}</span> · {fmtH(s.time)} → {fmtH(s.end_time)} · Poste {s.bay}
      </button>
    ))}
    {list.length === 0 && <p className="text-xs text-slate-500">Aucun créneau libre dans les 3 prochaines semaines.</p>}
  </div>
);

export const addMin = (t, d) => {
  const m = Math.min(parseInt(t.slice(0, 2), 10) * 60 + parseInt(t.slice(3, 5), 10) + d, 23 * 60 + 59);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

export const SlotSection = ({ slot, setSlot, onAvailability, excludeId = '', freeSlot = false }) => {
  const [custom, setCustom] = useState(!DURATIONS.some(([m]) => m === slot.duration));
  const [avail, setAvail] = useState(null);

  useEffect(() => {
    if (!slot.date || !slot.time || !slot.duration) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API}/agenda/availability`, { date: slot.date, time: slot.time, duration_min: slot.duration, exclude_id: excludeId })
        .then(r => { setAvail(r.data); onAvailability(r.data); })
        .catch(() => { setAvail(null); onAvailability(null); });
    }, 250);
    return () => clearTimeout(t);
  }, [slot.date, slot.time, slot.duration]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="space-y-3" data-testid="booking-slot-section">
      <div><Label>Date du rendez-vous</Label>
        <Input type="date" value={slot.date} onChange={e => setSlot(s => ({ ...s, date: e.target.value }))} data-testid="booking-date-input" /></div>
      <div>
        <Label>Durée</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {DURATIONS.map(([m, l]) => (
            <button key={m} type="button" data-testid={`duration-${m}`}
              onClick={() => { setCustom(false); setSlot(s => ({ ...s, duration: m })); }}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${!custom && slot.duration === m ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 hover:border-slate-900'}`}>{l}</button>
          ))}
          <button type="button" onClick={() => setCustom(true)} data-testid="duration-custom"
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${custom ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 hover:border-slate-900'}`}>Autre</button>
          {custom && (
            <div className="flex items-center gap-1">
              <Input type="number" min="0" className="w-16 h-8" value={Math.floor(slot.duration / 60)} data-testid="duration-custom-hours"
                onChange={e => setSlot(s => ({ ...s, duration: Math.max(15, (parseInt(e.target.value, 10) || 0) * 60 + (s.duration % 60)) }))} /><span className="text-sm">h</span>
              <Input type="number" min="0" max="45" step="15" className="w-16 h-8" value={slot.duration % 60} data-testid="duration-custom-minutes"
                onChange={e => setSlot(s => ({ ...s, duration: Math.max(15, Math.floor(s.duration / 60) * 60 + (parseInt(e.target.value, 10) || 0)) }))} /><span className="text-sm">min</span>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className="flex items-end justify-between gap-3 mb-1.5">
          <Label>Heures libres ce jour</Label>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">ou autre heure :
            <Input type="time" step="900" className="h-8 w-28" value={slot.time} onChange={e => setSlot(s => ({ ...s, time: e.target.value }))} data-testid="booking-time-input" /></div>
        </div>
        <FreeHours date={slot.date} duration={slot.duration} value={slot.time} excludeId={excludeId} onPick={(t) => setSlot(s => ({ ...s, time: t }))} />
      </div>
      {freeSlot && (
        <div className="rounded-xl p-3 border bg-teal-50 border-teal-200 text-sm font-semibold text-teal-800" data-testid="booking-rdv-slot">
          Rendez-vous de {fmtH(slot.time)} à {fmtH(addMin(slot.time, slot.duration))} · n'occupe pas l'atelier
        </div>
      )}
      {avail && !freeSlot && (
        <div className={`rounded-xl p-3 border ${avail.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="booking-availability">
          <div className="flex items-center gap-2 font-semibold text-sm">
            {avail.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-red-600" />}
            <span data-testid="booking-end-time">{avail.segments?.[0]?.start === slot.time && avail.segments?.[0]?.date === slot.date ? `Fin à ${fmtH(avail.end_time)}${avail.end_date !== slot.date ? ` (${fmtDate(avail.end_date)})` : ''}` : 'Créneau indisponible'}</span>
            {avail.ok && <span className="ml-auto text-xs bg-white px-2 py-0.5 rounded-full border" data-testid="booking-bay">Poste {avail.bay} / {avail.bays}</span>}
          </div>
          {avail.split && (
            <div className="mt-2 text-xs text-slate-700" data-testid="booking-split">
              <div className="flex items-center gap-1 font-semibold"><Scissors className="w-3 h-3" />Réparti sur les heures d'ouverture :</div>
              {avail.segments.map((s, i) => <div key={i} className="ml-4 capitalize">{fmtDate(s.date)} : {fmtH(s.start)} – {fmtH(s.end)}</div>)}
            </div>
          )}
          {!avail.ok && <p className="text-sm text-red-700 mt-1" data-testid="booking-conflict">{avail.reason}</p>}
          {!avail.ok && <Suggestions list={avail.suggestions} onPick={(s) => setSlot(x => ({ ...x, date: s.date, time: s.time }))} />}
        </div>
      )}
    </section>
  );
};
